// Mounts the call pill once for the whole app (app.jsx, next to
// LiveNotificationHandler). Renders nothing until a call arrives.
import { Component, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Button } from '@mui/material';
import { useSelector } from '../../redux/store';
import { useAuth } from '../../hooks';
import { PATH_DASHBOARD } from '../../routes/paths';
import CallPill, { CallPillDot } from './CallPill';
import useCallPill from './useCallPill';
import { ACTION, canLinkCaller, otherParty } from './callPillModel';
import { findOwedMissedCall } from './callPillOutcome';
import { CALL_PILL_ACTION_EVENT, isCallPillEnabled } from './callPillFlag';
import { stageFor } from './callPillStage';
import PillControls from '../outbound/PillControls';
import { useOutbound } from '../outbound/OutboundCallProvider';
import { deskLabel } from '../outbound/outboundModel';
import {
    SOUND_EVENT, markNotificationPermissionAsked, playRingTone, readSound, soundKey, unlockRingTone, writeDnd, writeSound,
} from './callPillSync';
import { requestPushPermission } from '../../sections/notifications/NotificationToast';
import { prefetchForCall } from './callPrefetch';
import { patientPath } from './patientPath';
import useCallOutcomes from './useCallOutcomes';
import { COULDNT_CONFIRM, WRITE, readWrite, undoCallOutcome } from './callOutcome';
import { useCallFlow } from '../callFlow/CallFlowProvider';
import { lazyWithRetries } from '../../routes/lazyRetry';
import CallReview from '../../pages/dashboard/callLogs/review/LazyCallReview';
import { pillRowId } from '../teamMember/teamMemberModel';

// Script: the existing call-script pop-ups (inbound / outbound), loaded on first use,
// with retries and inside its own boundary: the host sits right under the app-wide error
// boundary, so a stale chunk after a deploy must not blank the app.
const PillScript = lazyWithRetries(() => import('./PillScript'));
// The caller's stage screen (Record Attended, Follow up, the FTA "What's next?"). Loaded on
// first press for the same reason as the script: it pulls in every follow-up screen.
const PillStageScreen = lazyWithRetries(() => import('./PillStageScreen'));

class OutcomeBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch() {
        this.props.onFail();
    }

    render() {
        return this.state.failed ? null : this.props.children;
    }
}

OutcomeBoundary.propTypes = { onFail: PropTypes.func.isRequired, children: PropTypes.node };

export { patientPath };

// How often a call's prefetch is topped up while it shows (callPrefetch keeps answers 2 min).
const PREFETCH_KEEP_FRESH_MS = 60 * 1000;

// Where the bubble waits while a full-screen screen from the pill is open.
const BUBBLE_OVER_SCREEN = Object.freeze({ edge: 'right', offset: 1 });

// Any MUI dialog on screen: a call's details, a booking screen, a follow-up pop-up. The
// pill sits above MUI's modal layer (callLayers: snackbar + 1 beats a dialog's 1300), so
// without this it covers the dialog's own header and buttons. It waits as the bubble
// instead, exactly as it already does for its own script and Create lead screens; a click
// on the bubble brings it back over. MUI portals mount as direct children of <body>, so
// one childList observer there is enough — no subtree walk of the whole app.
const dialogOnScreen = () => {
    try {
        return Array.from(document.querySelectorAll('.MuiDialog-root'))
            // keepMounted dialogs stay in the DOM while closed, hidden.
            .some((el) => window.getComputedStyle(el).visibility !== 'hidden');
    } catch {
        return false;
    }
};

function useDialogOnScreen() {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const read = () => setOpen(dialogOnScreen());
        read();
        if (typeof MutationObserver !== 'function') return undefined;
        const observer = new MutationObserver(read);
        observer.observe(document.body, { childList: true });
        return () => observer.disconnect();
    }, []);
    return open;
}

export default function CallPillHost() {
    // The login user's pk (same id useGetCurrentUser matches on), available
    // without waiting for the practice's staff list.
    const { isAuthenticated, user } = useAuth();
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const members = useSelector((state) => state.practiceUser?.practiceUser?.results);
    const navigate = useNavigate();
    const userId = isAuthenticated ? user?.pk : null;

    const openCall = useCallback((call) => {
        if (!practiceId || !call) return;
        if (call.lead?.id) navigate(patientPath(practiceId, call.lead.id));
        else navigate(PATH_DASHBOARD.callLogs.practice(practiceId, { query: otherParty(call) }));
    }, [navigate, practiceId]);

    // My desk (CU-869fmcc9r): the desk this user holds names where calls ring ("Ringing at your desk (R TCO 2)").
    const outbound = useOutbound();
    const deskName = outbound?.myDesk ? deskLabel(outbound.myDesk) : '';
    // Internal words ("Simulator", test line names, the "Not linked" hint) are for DentalSEM
    // staff (superusers) only, never practice staff (CU-869fmcc9r).
    const pillAdmin = Boolean(user?.is_superuser);

    const pill = useCallPill({ userId, practiceId, enabled: isCallPillEnabled(), onNotificationClick: openCall, deskName });
    const createLeadOpen = useSelector((state) => Boolean(state.createLeadDialog?.open));

    // Log outcome / Create lead / Link: the app-level call flow host (components/callFlow).
    const callFlow = useCallFlow();
    const currentPractice = useRef(practiceId);
    useEffect(() => { currentPractice.current = practiceId; }, [practiceId]);
    // Log outcome: the outcome screen for the open missed call from this number
    // (callPillOutcome.js); nothing owed → Call Logs filtered to the number.
    const logOutcome = useCallback(async (call) => {
        const forPractice = practiceId;
        const number = otherParty(call);
        const startedOn = window.location.href;
        const { row } = await findOwedMissedCall(forPractice, number);
        if (String(currentPractice.current) !== String(forPractice)) return;
        if (!row) {
            // The user has moved on since the click: don't pull them away.
            if (window.location.href !== startedOn) return;
            navigate(PATH_DASHBOARD.callLogs.practice(forPractice, { query: number }));
            return;
        }
        // The pill's call was answered: the outcome screen leaves out No answer.
        callFlow.openOutcome(row, { afterCall: true });
    }, [callFlow, navigate, practiceId]);

    // Script: the call whose script pop-up is open.
    const [scriptFor, setScriptFor] = useState(null);
    // Stage screen: the call whose patient-stage pop-up is open.
    const [stageScreen, setStageScreen] = useState(null);
    // Review call (after hang-up): { practiceId, callId } of the call whose review is open.
    const [reviewFor, setReviewFor] = useState(null);
    const onReview = useCallback((call) => { if (practiceId && call?.callId) setReviewFor({ practiceId, callId: pillRowId(call) }); }, [practiceId]);
    // Calls where staff already pressed Create lead: never asked afterwards.
    const pressedCreate = useRef(new Set());

    // After my outbound call (CU-869fmbgfn): a toast with Undo for an automatic
    // no-answer log, or the outcome screen when it was answered and nothing was pressed.
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const undo = useCallback(async (forPractice, call, key) => {
        closeSnackbar(key);
        const result = readWrite(await undoCallOutcome(forPractice, call.callId), 'Could not undo.');
        if (result.kind === WRITE.OK) enqueueSnackbar('Undone: that try no longer counts.', { variant: 'success' });
        else enqueueSnackbar(result.kind === WRITE.UNKNOWN ? COULDNT_CONFIRM : result.message, { variant: 'warning' });
    }, [closeSnackbar, enqueueSnackbar]);
    useCallOutcomes({
        calls: pill.calls,
        practiceId,
        skipAsk: (call) => pressedCreate.current.has(String(call.callId)),
        onAutoLogged: (call, outcome) => {
            const forPractice = practiceId;
            callFlow.noteOwnCallAutoLogged(call);
            enqueueSnackbar(outcome.label, {
                variant: 'info',
                autoHideDuration: 10000,
                action: outcome.undoAllowed ? (key) => (
                    <Button size="small" color="inherit" onClick={() => undo(forPractice, call, key)} data-testid="outcome-undo">Undo</Button>
                ) : undefined,
            });
        },
        // Answered and nothing pressed: the same outcome screen as everywhere else (the backend's
        // options say whether Not a patient is allowed).
        onAsk: (call, outcome) => {
            if (outcome.options.length) callFlow.noteOwnCallNeedsOutcome(call, { options: outcome.options });
        },
    });

    const onAction = useCallback((actionId, call) => {
        if (actionId === ACTION.OPEN_PATIENT || actionId === ACTION.FIND_PATIENT) {
            // A caller we know is a patient at a stage opens that stage's own screen, the one
            // their Active Leads table opens: New -> Contact follow-up, Booked -> Record
            // Attended, Attended / FTA -> Follow up (Reschedule, Cancel booking). Treatment
            // started, closed, archived and a caller with no stage keep the lead page.
            const stage = actionId === ACTION.OPEN_PATIENT ? stageFor(call) : null;
            if (stage?.inPopup && practiceId) {
                // The stage screens fill the same lead store the Create lead form does.
                if (createLeadOpen) {
                    enqueueSnackbar('Finish or close Create lead first: it and this screen share the same details.', { variant: 'info' });
                    return;
                }
                setScriptFor(null);
                setStageScreen({ practiceId, call, stage, opened: Date.now() });
                return;
            }
            openCall(call);
            return;
        }
        // The script and the Create lead form share the lead store (opening the script resets
        // it), so one gives way to the other: Create lead closes the script; Script waits for
        // Create lead to close (CU-869fmcc9r).
        if (actionId === ACTION.CREATE_LEAD) {
            pressedCreate.current.add(String(call.callId));
            setScriptFor(null);
            if (practiceId) callFlow.openCreateLead(call);
            return;
        }
        if (actionId === ACTION.SCRIPT) {
            if (createLeadOpen) {
                enqueueSnackbar('Finish or close Create lead first: the script and the form share the same details.', { variant: 'info' });
                return;
            }
            if (practiceId) setScriptFor({ practiceId, call, opened: Date.now() });
            return;
        }
        // Log outcome / Call back: a screen that owns the flow can take it
        // (preventDefault). Otherwise Log outcome opens the outcome form, and
        // Call back opens Call Logs filtered to this number, where the call row
        // and its follow-up controls already live.
        const event = new CustomEvent(CALL_PILL_ACTION_EVENT, { cancelable: true, detail: { action: actionId, call, practiceId } });
        const unhandled = window.dispatchEvent(event);
        if (unhandled && practiceId) {
            if (actionId === ACTION.LOG_OUTCOME) logOutcome(call);
            else navigate(PATH_DASHBOARD.callLogs.practice(practiceId, { query: otherParty(call) }));
        }
        if (actionId === ACTION.LOG_OUTCOME) pill.dismiss(call.callId);
    }, [callFlow, createLeadOpen, enqueueSnackbar, logOutcome, navigate, openCall, pill.dismiss, practiceId]);

    // Call sound (CU-869fmcc9r): browsers only play sound after a click on the page, so the
    // user is asked once, in a small toast under the pill (never over it or the page): "Enable
    // call sound" is that click (it plays one ring to show it works) and also asks for desktop
    // notifications if the browser hasn't been asked yet. Turned on or off later in the pill's menu.
    const [soundChoice, setSoundChoice] = useState(() => readSound(userId));
    useEffect(() => {
        setSoundChoice(readSound(userId));
        const onSound = (e) => { if (String(e?.detail?.userId) === String(userId)) setSoundChoice(e.detail.on ? '1' : '0'); };
        const onStorage = (e) => { if (e.key === soundKey(userId)) setSoundChoice(e.newValue); };
        window.addEventListener(SOUND_EVENT, onSound);
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener(SOUND_EVENT, onSound);
            window.removeEventListener('storage', onStorage);
        };
    }, [userId]);
    const sound = soundChoice === '1';
    const toggleSound = useCallback((on) => {
        if (on) { unlockRingTone(); playRingTone(); }
        writeSound(userId, on);
    }, [userId]);
    const soundPrompt = useMemo(() => (soundChoice === null ? {
        onEnable: () => {
            toggleSound(true);
            if (typeof window !== 'undefined' && window.Notification?.permission === 'default') {
                markNotificationPermissionAsked(userId);
                requestPushPermission();
            }
        },
        // Asked once: "Not now" is an answer too (the menu's Call sound switch stays).
        onDismiss: () => writeSound(userId, false),
    } : null), [soundChoice, toggleSound, userId]);

    // Create lead and Script open within 500 ms of a press: what they need is fetched when a
    // call shows on the pill and again when it moves on (callPrefetch, CU-869fmcc9r).
    const prefetchedState = useRef(new Map());
    useEffect(() => {
        if (!practiceId) return;
        const seen = prefetchedState.current;
        pill.visible.forEach((call) => {
            const before = seen.get(call.callId);
            if (before === call.state) return;
            seen.set(call.callId, call.state);
            prefetchForCall(practiceId, call, { refresh: before !== undefined });
        });
    }, [pill.visible, practiceId]);
    useEffect(() => { prefetchedState.current = new Map(); }, [practiceId]);
    const visibleRef = useRef(pill.visible);
    visibleRef.current = pill.visible;
    const anyVisible = pill.visible.length > 0;
    useEffect(() => {
        if (!practiceId || !anyVisible) return undefined;
        const timer = setInterval(() => visibleRef.current.forEach((call) => prefetchForCall(practiceId, call)), PREFETCH_KEEP_FRESH_MS);
        return () => clearInterval(timer);
    }, [practiceId, anyVisible]);

    // "Link to existing patient" from the pill's menu: the call flow's Link dialog (UX-B).
    const onLinkLead = useCallback((call) => { callFlow.openLinkLead(call); }, [callFlow]);

    // While a full-screen screen from the pill is open (the script, Create lead) the pill waits as
    // the bubble in the bottom-right corner, so it never covers that screen; a click on the
    // bubble brings the pill back over it.
    const [pillOverScript, setPillOverScript] = useState(false);
    const dialogOpen = useDialogOnScreen();
    const fullScreenOpen = Boolean(scriptFor) || createLeadOpen || dialogOpen;
    useEffect(() => { if (!fullScreenOpen) setPillOverScript(false); }, [fullScreenOpen]);
    const tuckedForScript = fullScreenOpen && !pillOverScript;

    const onDotClick = useCallback(() => {
        if (tuckedForScript && !pill.minimised) {
            setPillOverScript(true);
            return;
        }
        if (pill.minimised) {
            pill.setMinimised(false);
            return;
        }
        if (pill.dnd && practiceId) {
            navigate(PATH_DASHBOARD.callLogs.practice(practiceId));
            return;
        }
        pill.unhideAll();
    }, [navigate, pill.dnd, pill.minimised, pill.setMinimised, pill.unhideAll, practiceId, tuckedForScript]);

    if (!userId || !practiceId) return null;
    // The same full-screen script the rest of the CRM opens (PillScript), not a panel of its own.
    const scriptDialog = scriptFor && String(scriptFor.practiceId) === String(practiceId) ? (
        <OutcomeBoundary key={`pill-script-${scriptFor.call.callId}-${scriptFor.opened}`} onFail={() => setScriptFor(null)}>
            <Suspense fallback={null}>
                <PillScript practiceId={scriptFor.practiceId} call={scriptFor.call} onClose={() => setScriptFor(null)} onOpenLead={openCall} />
            </Suspense>
        </OutcomeBoundary>
    ) : null;
    const stageDialog = stageScreen && String(stageScreen.practiceId) === String(practiceId) ? (
        <OutcomeBoundary key={`pill-stage-${stageScreen.call.callId}-${stageScreen.opened}`} onFail={() => setStageScreen(null)}>
            <Suspense fallback={null}>
                <PillStageScreen
                    practiceId={stageScreen.practiceId}
                    leadId={stageScreen.stage.leadId}
                    popup={stageScreen.stage.popup}
                    onClose={() => setStageScreen(null)}
                    // A stage with no pop-up of its own: the lead page, where its CTA lives.
                    onNeedsLeadPage={() => { setStageScreen(null); openCall(stageScreen.call); }}
                />
            </Suspense>
        </OutcomeBoundary>
    ) : null;
    // The same review as Call Logs; the pill waits as the bubble while it is open. It sits under the
    // app-wide boundary, so LazyCallReview carries its own.
    const reviewDialog = reviewFor && String(reviewFor.practiceId) === String(practiceId) ? (
        <CallReview callId={reviewFor.callId} list={[reviewFor.callId]} practiceId={reviewFor.practiceId} onClose={() => setReviewFor(null)} />
    ) : null;
    const pillPosition = pill.position;
    let body = null;
    if (pill.primary && (pill.minimised || tuckedForScript)) {
        // Waiting over a full-screen screen: the bottom-right corner, clear of its header and tabs.
        const dotPosition = tuckedForScript && !pill.minimised ? BUBBLE_OVER_SCREEN : pillPosition;
        // Minimised: the round bubble, pulsing while a call rings; a click expands it.
        body = (
            <CallPillDot
                count={pill.visible.length + pill.dotCount}
                minimised
                ringing={pill.visible.some((c) => c.state === 'ringing')}
                position={dotPosition}
                // The corner spot over a screen is not the user's place for the pill: not saved.
                onPositionChange={dotPosition === BUBBLE_OVER_SCREEN ? () => {} : pill.setPosition}
                onClick={onDotClick}
            />
        );
    } else if (pill.primary) {
        body = (
            <CallPill
                call={pill.primary}
                others={pill.others}
                hiddenCount={pill.dotCount}
                onShowHidden={onDotClick}
                onShowOthers={pill.focusNext}
                controls={<PillControls call={pill.primary} />}
                position={pillPosition}
                onPositionChange={pill.setPosition}
                onAction={onAction}
                onReview={onReview}
                onHide={pill.hide}
                onClose={pill.dismiss}
                deskName={deskName}
                isAdmin={pillAdmin}
                dnd={pill.dnd}
                onMinimise={() => (fullScreenOpen ? setPillOverScript(false) : pill.setMinimised(true))}
                onToggleDnd={(on) => writeDnd(userId, on)}
                sound={sound}
                onToggleSound={toggleSound}
                soundPrompt={soundPrompt}
                onChangeDesk={outbound?.canPickDesk ? () => outbound.openDeskPicker() : undefined}
                onLinkLead={canLinkCaller(pill.primary) ? onLinkLead : undefined}
                stageLabel={stageFor(pill.primary)?.label || ''}
                claimPracticeId={practiceId}
                claimPersonId={(members || []).find((m) => m?.user && userId != null && m.user.id === userId)?.id ?? null}
            />
        );
    } else if (pill.dotCount > 0) {
        body = (
            <CallPillDot
                count={pill.dotCount}
                dnd={pill.dnd}
                position={pillPosition}
                onPositionChange={pill.setPosition}
                onClick={onDotClick}
            />
        );
    }
    if (!body && !scriptDialog && !stageDialog && !reviewDialog) return null;
    return <>{body}{scriptDialog}{stageDialog}{reviewDialog}</>;
}
